import React from "react";
import { Tag, Space, Button } from "antd";
import { SelectOutlined, DownloadOutlined } from "@ant-design/icons";
import DataTable from "../../../components/DataTable";
import { exportPipeline } from "./SkeyeingestPipelinesAPI";
import { extractMsg } from "../../../utils/errorUtils";

export default function SkeyeingestPipelinesTable({
  data,
  loading,
  page,
  pageSize,
  onPageChange,
  messageApi,
}) {
  const env = window._env_ || {};
  const skeyeingestPort = env.SKEYEINGEST_PORT || "18630";
  const currentUrl = window.location.origin;
  const baseUrl = currentUrl.split(":").slice(0, 2).join(":");
  const skeyeingestUrl = env.SKEYEINGEST_URL || `${baseUrl}:${skeyeingestPort}`;

  const handleDownload = async (pipelineId) => {
    try {
      messageApi.loading({
        content: `Exporting pipeline ${pipelineId}...`,
        key: "export",
      });
      const response = await exportPipeline(pipelineId);
      const blob = new Blob([JSON.stringify(response.data.pipeline, null, 2)], {
        type: "application/json",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${pipelineId}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      messageApi.success({
        content: "Pipeline exported successfully",
        key: "export",
      });
    } catch (error) {
      messageApi.error({
        content: extractMsg(error, "Failed to export pipeline"),
        key: "export-error",
      });
    }
  };

  const columns = [
    {
      title: "Pipeline ID",
      dataIndex: "pipelineId",
      key: "pipelineId",
      width: "15%",
      sorter: (a, b) => (a.pipelineId || "").localeCompare(b.pipelineId || ""),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: "20%",
      sorter: (a, b) => (a.title || "").localeCompare(b.title || ""),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "15%",
      render: (status) => {
        let color = "default";
        if (status === "RUNNING") color = "success";
        else if (status === "STOPPED" || status === "EDITED") color = "warning";
        else if (status === "START_ERROR" || status === "RUN_ERROR")
          color = "error";
        return <Tag color={color}>{status}</Tag>;
      },
      sorter: (a, b) => (a.status || "").localeCompare(b.status || ""),
    },
    {
      title: "Valid?",
      dataIndex: "valid",
      key: "valid",
      width: "10%",
      render: (valid) => (
        <Tag color={valid ? "success" : "error"}>
          {valid ? "Valid" : "Invalid"}
        </Tag>
      ),
      sorter: (a, b) => (a.valid === b.valid ? 0 : a.valid ? -1 : 1),
    },
    {
      title: "Created",
      dataIndex: "created",
      key: "created",
      width: "15%",
      render: (ts) => (ts ? new Date(Number(ts)).toLocaleString() : ""),
      sorter: (a, b) => Number(a.created || 0) - Number(b.created || 0),
    },
    {
      title: "Last Modified",
      dataIndex: "lastModified",
      key: "lastModified",
      width: "15%",
      render: (ts) => (ts ? new Date(Number(ts)).toLocaleString() : ""),
      sorter: (a, b) =>
        Number(a.lastModified || 0) - Number(b.lastModified || 0),
    },
    {
      title: "Actions",
      key: "actions",
      width: "10%",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="text"
            icon={<SelectOutlined />}
            onClick={() =>
              window.open(
                `${skeyeingestUrl}/collector/pipeline/${record.pipelineId}/`,
                "_blank",
                "noopener,noreferrer",
              )
            }
            title="Access Pipeline"
            style={{ color: "#e29b45" }}
          />
          <Button
            type="text"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(record.pipelineId)}
            title="Download Pipeline"
            style={{ color: "#D97A07" }}
          />
        </Space>
      ),
    },
  ];

  return (
    <DataTable
      rowKey="pipelineId"
      columns={columns}
      data={data}
      loading={loading}
      page={page}
      pageSize={pageSize}
      onPageChange={onPageChange}
    />
  );
}
